import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiJson } from "./_http";

// Typed React Query hooks for the market-data BFF (functions/api/market/*).
// The proxies are public — no session is required — and the upstream Worker
// caches aggressively in KV, so we only debounce here implicitly via the
// staleTime / refetchInterval pair.
//
// Symbol normalization: we uppercase + trim before keying so the local cache
// matches what the upstream produces (the Worker also uppercases). Avoids
// duplicate fetches for "aapl" / "AAPL " / "AAPL".

export interface Quote {
	symbol: string;
	last: number;
	bidPrice: number;
	askPrice: number;
	volume: number;
	change: number;
	changePct: number;
	asOf: string;
}

export interface Bar {
	symbol: string;
	open: number;
	high: number;
	low: number;
	close: number;
	volume: number;
	time: string;
}

export interface Ticker {
	symbol: string;
	name: string;
	exchange: string;
	assetType: string;
	marketCapUsd: number;
	active: boolean;
	meetsFloor: boolean;
	marketCapTier: string | null;
}

export interface Fundamentals {
	symbol: string;
	peRatio: number;
	dividendYield: number;
	marketCap: number;
	sharesOutstanding: number;
	sector: string;
}

interface QuotesBatchResponse {
	quotes: Record<string, Quote>;
	stale?: boolean;
}

interface BarsResponse {
	symbol: string;
	bars: Bar[];
}

export type ChartRange = "1D" | "1W" | "1M" | "3M" | "1Y" | "5Y";

interface RangeSpec {
	timeframe: string;
	limit: number;
}

// Range → (timeframe, limit) lookup. Numbers are chosen to keep payloads
// small while covering the visible window:
//   1D — 78 5-minute bars covers a 6.5h US trading day
//   1W — 40 hourly bars covers ~one trading week
//   1M — 22 daily bars covers ~one trading month
//   3M — 66 daily bars
//   1Y — 252 daily bars (US trading days/year)
//   5Y — 260 weekly bars (keeps the payload ~10× smaller than 1300 dailies)
const RANGE_SPECS: Record<ChartRange, RangeSpec> = {
	"1D": { timeframe: "5m", limit: 78 },
	"1W": { timeframe: "1h", limit: 40 },
	"1M": { timeframe: "1d", limit: 22 },
	"3M": { timeframe: "1d", limit: 66 },
	"1Y": { timeframe: "1d", limit: 252 },
	"5Y": { timeframe: "1w", limit: 260 },
};

export function rangeSpec(range: ChartRange): RangeSpec {
	return RANGE_SPECS[range];
}

function normalizeSymbol(s: string): string {
	return s.trim().toUpperCase();
}

/**
 * Live-ish quote for a single symbol. 30s staleTime + 60s refetch — short
 * enough to feel real-time, long enough to not stress the upstream's KV
 * coalesce path. Quotes are cheap on the wire (~150 bytes each) so we don't
 * worry about churn.
 */
export function useQuote(symbol: string | null | undefined) {
	const norm = symbol ? normalizeSymbol(symbol) : "";
	return useQuery<Quote>({
		queryKey: ["market", "quote", norm],
		queryFn: () => apiJson<Quote>(`/api/market/quote/${encodeURIComponent(norm)}`),
		enabled: norm.length > 0,
		staleTime: 30_000,
		refetchInterval: 60_000,
		// Don't refetch on mount if we have data <30s old — composite views
		// remount on tab switches and we don't want a thundering herd.
		refetchOnMount: false,
	});
}

/**
 * Batch quotes for a list of symbols. Single fetch keyed on the sorted symbol
 * tuple so different orderings hit the same cache slot. Returns the wire
 * `{quotes: Record<symbol, Quote>}` shape unchanged so callers can do
 * `data?.quotes[sym]` directly.
 */
export function useQuotes(symbols: string[]): UseQueryResult<QuotesBatchResponse> {
	// Dedupe + sort so order-independent inputs share a cache key.
	const cleaned = Array.from(
		new Set(symbols.map((s) => normalizeSymbol(s)).filter((s) => s.length > 0)),
	).sort();

	return useQuery<QuotesBatchResponse>({
		queryKey: ["market", "quotes", cleaned],
		queryFn: () => {
			const qs = new URLSearchParams({ symbols: cleaned.join(",") }).toString();
			return apiJson<QuotesBatchResponse>(`/api/market/quotes?${qs}`);
		},
		enabled: cleaned.length > 0,
		staleTime: 30_000,
		refetchInterval: 60_000,
		refetchOnMount: false,
	});
}

/**
 * Historical OHLC bars for a range. The range argument is part of the query
 * key so flipping the tab swaps caches cleanly.
 */
export function useBars(symbol: string | null | undefined, range: ChartRange) {
	const norm = symbol ? normalizeSymbol(symbol) : "";
	const spec = RANGE_SPECS[range];
	return useQuery<BarsResponse>({
		queryKey: ["market", "bars", norm, range],
		queryFn: () => {
			const qs = new URLSearchParams({
				timeframe: spec.timeframe,
				limit: String(spec.limit),
			}).toString();
			return apiJson<BarsResponse>(
				`/api/market/bars/${encodeURIComponent(norm)}?${qs}`,
			);
		},
		enabled: norm.length > 0,
		// Bars are upstream-cached for an hour; 5min staleTime mirrors that
		// without the wall-clock drift problem.
		staleTime: 5 * 60_000,
	});
}

/**
 * Ticker reference row — name, exchange, market cap. Slow-changing data;
 * 5min stale is plenty.
 */
export function useTicker(symbol: string | null | undefined) {
	const norm = symbol ? normalizeSymbol(symbol) : "";
	return useQuery<Ticker>({
		queryKey: ["market", "ticker", norm],
		queryFn: () => apiJson<Ticker>(`/api/market/ticker/${encodeURIComponent(norm)}`),
		enabled: norm.length > 0,
		staleTime: 5 * 60_000,
	});
}

/**
 * Fundamentals — refreshed daily upstream. 1h stale on the client.
 */
export function useFundamentals(symbol: string | null | undefined) {
	const norm = symbol ? normalizeSymbol(symbol) : "";
	return useQuery<Fundamentals>({
		queryKey: ["market", "fundamentals", norm],
		queryFn: () =>
			apiJson<Fundamentals>(`/api/market/fundamentals/${encodeURIComponent(norm)}`),
		enabled: norm.length > 0,
		staleTime: 60 * 60_000,
	});
}

// --- Earnings calendar ------------------------------------------------------

export interface EarningsWeek {
	week: string; // ISO week, "YYYY-Www"
	count: number; // number of tickers reporting that week
	startDate: string; // Monday, YYYY-MM-DD
	endDate: string; // Sunday, YYYY-MM-DD
}

interface EarningsWeeksResponse {
	weeks: EarningsWeek[];
}

/**
 * Upcoming earnings weeks — feeds the Create Fantasy Fam earnings-week picker.
 * Backed by a daily-refreshed CSV upstream, so 30min stale on the client is
 * plenty.
 */
export function useEarningsWeeks(): UseQueryResult<EarningsWeeksResponse> {
	return useQuery<EarningsWeeksResponse>({
		queryKey: ["market", "earnings-weeks"],
		queryFn: () => apiJson<EarningsWeeksResponse>("/api/market/earnings-weeks"),
		staleTime: 30 * 60_000,
	});
}

// --- Error helpers ----------------------------------------------------------

/**
 * Returns true when an ApiClientError-like value indicates the upstream is
 * temporarily unavailable (typical when POLYGON_API_KEY is unset). Callers
 * use this to switch to the "Market data temporarily unavailable" empty state
 * rather than crash.
 */
export function isMarketUnavailable(err: unknown): boolean {
	if (!err || typeof err !== "object") return false;
	const e = err as { code?: unknown; status?: unknown };
	if (typeof e.code === "string") {
		if (
			e.code === "upstream_unavailable" ||
			e.code === "upstream_error" ||
			e.code === "rate_limited"
		) {
			return true;
		}
	}
	if (typeof e.status === "number") {
		if (e.status === 502 || e.status === 503 || e.status === 429) return true;
	}
	return false;
}
